import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { SubscriptionActivationDialog } from '@/account/subscription-activation/components/SubscriptionActivationDialog'
import { useSubscriptionActivation } from '@/account/subscription-activation/hooks/useSubscriptionActivation'
import { AuthContext } from '@/context/auth-context'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import { OcrQuotaMeter } from '@/ocr_quota/components/OcrQuotaMeter'
import { OcrQuotaReachedModal } from '@/ocr_quota/components/OcrQuotaReachedModal'
import { useOcrQuotaSummary } from '@/ocr_quota/hooks/useOcrQuotaSummary'
import { useOcrQuotaWaiterRefresh } from '@/ocr_quota/hooks/useOcrQuotaWaiterRefresh'
import { OcrQuotaPolicy } from '@/ocr_quota/schemas'
import { selectAcknowledgedQuotaAllowances } from '@/ocr_quota/store/selectors'
import { OcrQuotaUiActions } from '@/ocr_quota/store/slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useProcessingSummary } from '@/uppy-file-upload/hooks/useProcessingSummary'
import { selectUploadDrawerFiles } from '@/uppy-file-upload/store/selectors'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { selectCurrentFolderDriveVisibleFiles } from '@/user_files/files/store/selectors'
import { isWaitingForOcrQuota } from '@/user_files/utils/processing-status'

type QuotaNotice = {
  signature: string
  variant: 'exhausted' | 'document-does-not-fit'
  remainingPages: number | null
}

/** Owns the OCR quota lifecycle shown above a matter's Drive contents. */
export function MatterOcrQuotaStatus({ deferNotice = false, syncedFiles }: { deferNotice?: boolean; syncedFiles?: UserFileRead[] }) {
  const dispatch = useAppDispatch()
  const { user, userAccountData } = useContext(AuthContext)
  const matterFiles = useAppSelector(selectCurrentFolderDriveVisibleFiles)
  const files = syncedFiles ?? matterFiles
  const { isOpen: supportOpen, toggleWidget } = useHubspotConversations()
  const contactSupport = () => {
    if (!supportOpen) toggleWidget()
  }
  const uploadDrawerFiles = useAppSelector(selectUploadDrawerFiles)
  const acknowledgedAllowances = useAppSelector(selectAcknowledgedQuotaAllowances)
  const { readyCount } = useProcessingSummary()
  const { summary, requestedAt, loading, error, refresh } = useOcrQuotaSummary()
  const [notice, setNotice] = useState<QuotaNotice | null>(null)
  const isTrial = summary?.is_trial === true

  const quotaBlockedFileIds = useMemo(() => {
    const fileIds = new Set(files.filter(isWaitingForOcrQuota).map((file) => file.id))

    uploadDrawerFiles.forEach((file) => {
      if (!file.backend || !isWaitingForOcrQuota(file.backend.record)) return
      if (file.client.serverFileId) fileIds.add(file.client.serverFileId)
    })

    return [...fileIds].sort()
  }, [files, uploadDrawerFiles])

  const refreshQuotaWaiters = useOcrQuotaWaiterRefresh({
    fileIds: quotaBlockedFileIds,
    refreshQuota: refresh,
  })
  // Activation can release queued files, so their rows refresh along with usage.
  const activation = useSubscriptionActivation(refreshQuotaWaiters)

  // One notice per account/allowance, not one interruption per file finishing in a batch.
  const allowanceSignature =
    summary?.policy === OcrQuotaPolicy.Cumulative
      ? JSON.stringify([user?.uid, userAccountData?.orgData?.id, summary.subject_type, summary.effective_at, summary.page_limit])
      : null
  const needsNotice = quotaBlockedFileIds.length > 0
  const quotaRefreshSignal = `${readyCount}:${quotaBlockedFileIds.join(',')}`
  const previousQuotaRefreshSignalRef = useRef(quotaRefreshSignal)
  const previousBlockedFileIdsRef = useRef(quotaBlockedFileIds)
  // A summary requested before files joined the blocked set can predate them, so it must not open the notice.
  const blockedFilesAddedAtRef = useRef<number | null>(null)

  // Declared before the notice effect so newly blocked files are recorded before a summary is judged.
  useEffect(() => {
    if (previousQuotaRefreshSignalRef.current === quotaRefreshSignal) return
    previousQuotaRefreshSignalRef.current = quotaRefreshSignal
    const previousBlockedFileIds = previousBlockedFileIdsRef.current
    previousBlockedFileIdsRef.current = quotaBlockedFileIds
    if (quotaBlockedFileIds.some((fileId) => !previousBlockedFileIds.includes(fileId))) blockedFilesAddedAtRef.current = Date.now()
    void refresh()
  }, [quotaRefreshSignal, quotaBlockedFileIds, refresh])

  useEffect(() => {
    const blockedFilesAddedAt = blockedFilesAddedAtRef.current
    const summaryIsCurrent = blockedFilesAddedAt === null || (requestedAt !== null && requestedAt >= blockedFilesAddedAt)
    if (
      notice ||
      deferNotice ||
      loading ||
      error ||
      !summary ||
      !summaryIsCurrent ||
      !allowanceSignature ||
      !needsNotice ||
      acknowledgedAllowances.includes(allowanceSignature)
    )
      return
    // Snapshot the message until acknowledgement: background refreshes must not replace or dismiss it.
    setNotice({ signature: allowanceSignature, variant: summary.is_exhausted ? 'exhausted' : 'document-does-not-fit', remainingPages: summary.remaining_pages })
  }, [notice, deferNotice, loading, error, summary, requestedAt, allowanceSignature, needsNotice, acknowledgedAllowances])

  // Released capacity can admit queued files, so check them now instead of at the next scheduled refresh.
  const previousSummaryRef = useRef(summary)
  useEffect(() => {
    const previousSummary = previousSummaryRef.current
    previousSummaryRef.current = summary
    if (!previousSummary || !summary || quotaBlockedFileIds.length === 0) return
    const remainingPagesRose =
      previousSummary.remaining_pages !== null && (summary.remaining_pages === null || summary.remaining_pages > previousSummary.remaining_pages)
    if (remainingPagesRose || summary.waiting.known_files < previousSummary.waiting.known_files) void refreshQuotaWaiters()
  }, [summary, quotaBlockedFileIds.length, refreshQuotaWaiters])

  return (
    <>
      <OcrQuotaMeter
        summary={summary}
        isTrial={isTrial}
        onContactSupport={contactSupport}
        onActivateSubscription={activation.onActivateSubscription}
        hasQueuedSyncedFiles={syncedFiles?.some(isWaitingForOcrQuota)}
      />

      <OcrQuotaReachedModal
        showUsage={isTrial && summary?.policy === OcrQuotaPolicy.Cumulative}
        open={notice !== null && !deferNotice}
        variant={notice?.variant ?? 'exhausted'}
        remainingPages={notice?.remainingPages ?? null}
        onContactSupport={contactSupport}
        onActivateSubscription={activation.onActivateSubscription}
        onDismiss={() => {
          if (notice) dispatch(OcrQuotaUiActions.acknowledgeQuotaModal(notice.signature))
          setNotice(null)
        }}
      />
      <SubscriptionActivationDialog {...activation} />
    </>
  )
}
